/**
 * Auth state (P1-T11). Tokens live in localStorage via api/client.ts;
 * this module keeps the hydrated `User` and notifies listeners on change.
 */
import { attributionPayload } from '../marketing/attribution.js';
import type { User } from '@loomia/shared';
import { api, loadTokens } from '../api/client.js';
import { Emitter } from './events.js';

/** Fired whenever the signed-in user changes (login, logout, expiry). */
export const authChanged = new Emitter();

let currentUser: User | null = null;

export function getUser(): User | null {
  return currentUser;
}

/** True when ANY session exists, including an anonymous guest session. */
export function isAuthed(): boolean {
  return loadTokens() !== null;
}

/** Guest sessions are real accounts with a reserved email domain (see the API). */
export function isGuest(): boolean {
  return currentUser !== null && currentUser.email.toLowerCase().endsWith('@guest.invalid');
}

/** True when the user has a real account (signed in with email), not just a guest session. */
export function hasAccount(): boolean {
  return isAuthed() && !isGuest();
}

let guestInflight: Promise<boolean> | null = null;

/**
 * Make sure a session exists so uploads work before sign-in. Creates an
 * anonymous guest session when there is none. Safe to call repeatedly.
 */
export function ensureGuestSession(): Promise<boolean> {
  if (isAuthed()) return Promise.resolve(true);
  guestInflight ??= api
    .createGuest(attributionPayload())
    .then((res) => {
      setUser(res.user);
      return true;
    })
    .catch(() => false)
    .finally(() => {
      guestInflight = null;
    });
  return guestInflight;
}

// Signing in on another tab (magic link) swaps the tokens in localStorage.
// Pick that up here so the tab the user was working in becomes signed in
// without a reload, keeping whatever they had on screen.
window.addEventListener('storage', (e) => {
  if (e.key !== null && !e.key.startsWith('loomia.')) return;
  void initAuth();
});

function setUser(user: User | null): void {
  currentUser = user;
  authChanged.emit();
}

/**
 * Validate the stored session on boot. Returns true when the user is signed
 * in with a working session (refreshing the access token if needed).
 */
export async function initAuth(): Promise<boolean> {
  if (!isAuthed()) {
    setUser(null);
    return false;
  }
  try {
    const { user } = await api.me();
    setUser(user);
    return true;
  } catch {
    // 401 path clears tokens + fires onUnauthorized (wired in main.ts).
    setUser(null);
    return false;
  }
}

/** Called after a successful magic-link / OAuth verification. */
export function setUserAfterSignIn(user: User): void {
  setUser(user);
}

/** Best-effort server logout, then drop local state. */
export async function signOut(): Promise<void> {
  try {
    await api.logout();
  } catch {
    // Tokens are cleared by logout() regardless; don't block the UI.
  }
  setUser(null);
}

// ---------------------------------------------------------------------------
// "Sign in, then continue where you were going"
//
// Guests browse freely; login is only required for actions (generate, collection,
// paywall, settings). The wanted route is remembered in localStorage (not
// sessionStorage) because the magic link usually opens in a different tab.
// ---------------------------------------------------------------------------

const RETURN_KEY = 'loomia.returnTo';
const RETURN_TTL_MS = 30 * 60 * 1000;

/** Remember a hash path like `#/editor/change_outfit?x=1`. Auth pages are ignored. */
export function setReturnTo(hash: string): void {
  if (!hash.startsWith('#/') || /^#\/(login|auth\/verify)/.test(hash)) return;
  try {
    localStorage.setItem(RETURN_KEY, JSON.stringify({ hash, at: Date.now() }));
  } catch {
    /* storage unavailable: fall back to Home after login */
  }
}

/** Returns the remembered path (without `#`) once, or null. */
export function takeReturnTo(): string | null {
  try {
    const raw = localStorage.getItem(RETURN_KEY);
    localStorage.removeItem(RETURN_KEY);
    if (!raw) return null;
    const { hash, at } = JSON.parse(raw) as { hash: string; at: number };
    if (typeof hash !== 'string' || Date.now() - at > RETURN_TTL_MS) return null;
    return hash.slice(1);
  } catch {
    return null;
  }
}
